import React, { useState, useEffect } from 'react';
import { Cloud, CloudRain, CloudSnow, Sun, Wind, Droplets, Eye } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../../../contexts/ThemeContext';

const DateWeatherDisplay = () => {
  const { isDarkMode } = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);

  // Update date every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Fetch weather data
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get user's location
        if (!navigator?.geolocation) {
          throw new Error('Geolocation is not supported by your browser');
        }

        navigator.geolocation?.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position?.coords;
            setLocation({ lat: latitude, lon: longitude });

            // Fetch weather from OpenWeatherMap API
            const apiKey = import.meta.env?.VITE_OPENWEATHER_API_KEY;
            
            if (!apiKey || apiKey === 'your-openweather-api-key-here') {
              // Use mock data if no API key
              setWeather({
                temp: 22,
                feelsLike: 21,
                description: 'Partly cloudy',
                humidity: 65,
                windSpeed: 15,
                visibility: 10,
                icon: '02d',
                city: 'Your City'
              });
              setLoading(false);
              return;
            }

            const response = await axios?.get(
              `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&appid=${apiKey}&units=metric`
            );

            setWeather({
              temp: Math.round(response?.data?.main?.temp),
              feelsLike: Math.round(response?.data?.main?.feels_like),
              description: response?.data?.weather?.[0]?.description,
              humidity: response?.data?.main?.humidity,
              windSpeed: Math.round(response?.data?.wind?.speed * 3.6), // Convert m/s to km/h
              visibility: Math.round(response?.data?.visibility / 1000), // Convert to km
              icon: response?.data?.weather?.[0]?.icon,
              city: response?.data?.name
            });
            setLoading(false);
          },
          (error) => {
            console.error('Geolocation error:', error);
            // Use mock data on location error
            setWeather({
              temp: 22,
              feelsLike: 21,
              description: 'Partly cloudy',
              humidity: 65,
              windSpeed: 15,
              visibility: 10,
              icon: '02d',
              city: 'Your City'
            });
            setLoading(false);
          }
        );
      } catch (err) {
        console.error('Weather fetch error:', err);
        setError(err?.message);
        setLoading(false);
      }
    };

    fetchWeather();
    // Refresh weather every 30 minutes
    const weatherInterval = setInterval(fetchWeather, 1800000);

    return () => clearInterval(weatherInterval);
  }, []);

  const formatDate = () => {
    return currentDate?.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = () => {
    return currentDate?.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getWeatherIcon = (iconCode) => {
    if (!iconCode) return <Sun className="w-8 h-8 text-yellow-500" />;

    const code = iconCode?.substring(0, 2);
    switch (code) {
      case '01':
        return <Sun className="w-8 h-8 text-yellow-500" />;
      case '02': case'03': case'04':
        return <Cloud className="w-8 h-8 text-gray-400" />;
      case '09': case'10':
        return <CloudRain className="w-8 h-8 text-blue-500" />;
      case '13':
        return <CloudSnow className="w-8 h-8 text-blue-300" />;
      case '50':
        return <Wind className="w-8 h-8 text-gray-500" />;
      default:
        return <Sun className="w-8 h-8 text-yellow-500" />;
    }
  };

  return (
    <div className={`rounded-lg shadow-md p-6 mb-6 transition-colors ${
      isDarkMode ? 'bg-gray-800' : 'bg-white'
    }`}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        {/* Date and Time Section */}
        <div className="flex-1">
          <div className={`text-sm mb-1 ${
            isDarkMode ? 'text-gray-400' : 'text-muted-foreground'
          }`}>Today</div>
          <div className={`text-2xl font-semibold mb-1 ${
            isDarkMode ? 'text-white' : 'text-foreground'
          }`}>
            {formatDate()}
          </div>
          <div className={`text-lg ${
            isDarkMode ? 'text-gray-400' : 'text-muted-foreground'
          }`}>
            {formatTime()}
          </div>
        </div>

        {/* Weather Section */}
        <div className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-sm text-red-500">
              Unable to load weather
            </div>
          ) : weather ? (
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                {getWeatherIcon(weather?.icon)}
              </div>
              <div className="flex-1">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className={`text-3xl font-bold ${
                    isDarkMode ? 'text-white' : 'text-foreground'
                  }`}>
                    {weather?.temp}°C
                  </span>
                  <span className={`text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-muted-foreground'
                  }`}>
                    Feels like {weather?.feelsLike}°C
                  </span>
                </div>
                <div className={`text-sm capitalize mb-2 ${
                  isDarkMode ? 'text-gray-400' : 'text-muted-foreground'
                }`}>
                  {weather?.description}
                </div>
                <div className={`text-xs ${
                  isDarkMode ? 'text-gray-500' : 'text-muted-foreground'
                }`}>
                  {weather?.city}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Weather Details */}
        {weather && !loading && !error && (
          <div className="flex-1">
            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col items-center">
                <Droplets className="w-5 h-5 text-blue-500 mb-1" />
                <span className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-muted-foreground'
                }`}>Humidity</span>
                <span className={`text-sm font-semibold ${
                  isDarkMode ? 'text-white' : 'text-foreground'
                }`}>{weather?.humidity}%</span>
              </div>
              <div className="flex flex-col items-center">
                <Wind className="w-5 h-5 text-gray-500 mb-1" />
                <span className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-muted-foreground'
                }`}>Wind</span>
                <span className={`text-sm font-semibold ${
                  isDarkMode ? 'text-white' : 'text-foreground'
                }`}>{weather?.windSpeed} km/h</span>
              </div>
              <div className="flex flex-col items-center">
                <Eye className="w-5 h-5 text-purple-500 mb-1" />
                <span className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-muted-foreground'
                }`}>Visibility</span>
                <span className={`text-sm font-semibold ${
                  isDarkMode ? 'text-white' : 'text-foreground'
                }`}>{weather?.visibility} km</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateWeatherDisplay;